import {type StripeGateway, type IStripeContext} from '../../../models/stripe';
import {$qs} from '../../../../../@shared/ts/dom';
import {PeachPayCustomer} from '../../../reducers/peachPayCustomerReducer';
import {MerchantConfiguration} from '../../../reducers/merchantConfigurationReducer';
import {getLocaleText} from '../../../util/translation';
import imageBadge from '../../../../img/marks/afterpay-logo.svg';
import afterpayImageURL from '../../../../img/marks/afterpay.svg';
import clearpayImageURL from '../../../../img/marks/clearpay.svg';
import {Feature} from '../../../reducers/environmentReducer';
import {FeatureFlag} from '../../../../../@type/features';
import {type GatewayConfiguration} from '../../../models/GatewayConfiguration';

function initStripeAfterPayMethod(): StripeGateway {
	const config: GatewayConfiguration = {
		name: getLocaleTitle(),
		gatewayId: 'peachpay_stripe_afterpay',
		description: getLocaleText('After selecting pay you will be redirected to complete your payment.'),
		assets: {
			title: {src: getLocaleImage()},
			badge: {src: imageBadge},
		},
	};

	self.addEventListener('pp-update-afterpay-branding', async () => {
		const afterpayLocale = updateAfterpayBranding();

		config.name = afterpayLocale.name;
		if (config.assets.title) {
			config.assets.title.src = afterpayLocale.titleImage.length
				? afterpayLocale.titleImage : config.assets.title.src;
		}
	});

	return {
		config,
		async tokenize(context) {
			const {stripe} = context;

			const {paymentMethod, error} = await stripe.createPaymentMethod({
				type: 'afterpay_clearpay',
				billing_details: PeachPayCustomer.stripeBillingDetails(),
			});

			if (error) {
				const message = error.message ?? 'Failed to tokenize payment method.';

				return [null, {
					paymentStatus: 'creationfailed',
					note: message,
				}];
			}

			if (!paymentMethod?.id) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to tokenize payment method.',
				}];
			}

			return [paymentMethod.id, {}];
		},
		async confirm(context: IStripeContext, clientSecret: string, intermediateURL: string) {
			const {stripe} = context;

			const {paymentIntent, error} = await stripe.confirmAfterpayClearpayPayment(clientSecret, {return_url: intermediateURL});

			if (error) {
				const message = error.message ?? 'Failed to confirm payment.';
				return [null, {
					note: message,
				}];
			}

			if (paymentIntent) {
				return ['success', {}];
			}

			return [null, {}];
		},
	};
}

function getLocaleTitle(countryOverride: string | null = null): string {
	const title = Feature.metadata<string>(FeatureFlag.STRIPE_AFTERPAY_GATEWAY, 'title');
	if (title && !(title === 'Afterpay' || title === 'Clearpay')) {
		return title;
	}

	if (countryOverride) {
		return countryOverride;
	}

	let country: string | undefined = PeachPayCustomer.billing.country();
	if (!country && MerchantConfiguration.general.wcLocationInfoData) {
		country = MerchantConfiguration.general.wcLocationInfoData()?.store_country;
	}

	switch (country) {
		case 'GB':
		case 'ES':
		case 'FR':
		case 'IT': {
			return 'Clearpay';
		}

		default: {
			return 'Afterpay';
		}
	}
}

function getLocaleImage(countryOverride: string | null = null): string {
	if (countryOverride) {
		return countryOverride;
	}

	let country: string | undefined = PeachPayCustomer.billing.country();
	if (!country && MerchantConfiguration.general.wcLocationInfoData) {
		country = MerchantConfiguration.general.wcLocationInfoData()?.store_country;
	}

	switch (country) {
		case 'GB':
		case 'ES':
		case 'FR':
		case 'IT': {
			return clearpayImageURL;
		}

		default: {
			return afterpayImageURL;
		}
	}
}

function updateAfterpayBranding(countryOverride: string | null = null): {name: string; titleImage: string} {
	const title = getLocaleTitle(countryOverride);

	$qs('#pp-pms-new .pp-pm-type[data-gateway="peachpay_stripe_afterpay"] > span', el => {
		el.innerHTML = title;
	});
	$qs('#pp-pms-new .pp-pm-more li[data-gateway="peachpay_stripe_afterpay"] > span', el => {
		el.innerHTML = title;
	});

	const imageURL = getLocaleImage(countryOverride);

	return {
		name: title,
		titleImage: imageURL,
	};
}

export {initStripeAfterPayMethod};
